import type { ORPCClient, Outputs } from '@/lib/orpc'
import { useState } from 'react'
import { useForm } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { Key } from 'lucide-react'
import { toast } from 'sonner'
import z from 'zod'

import { Button } from '../ui/button'
import { CopyButton } from '../ui/copy-button'
import { Field, FieldDescription, FieldError, FieldGroup } from '../ui/field'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput
} from '../ui/input-group'
import { Spinner } from '../ui/spinner'

const formSchema = z.object({
  apiKey: z.string()
})

interface RotateApiKeyFormProps {
  initialApiKey: Outputs['backoffice']['apiKeys']['getAll'][number]
  onClose?: (apiKeyId: string) => void
  orpc: ORPCClient
}

export default function RotateApiKeyForm({
  initialApiKey,
  onClose,
  orpc
}: RotateApiKeyFormProps) {
  const [apiKey, setApiKey] = useState(initialApiKey.key)
  const [obscured, setObscured] = useState(true)

  const rotate = useMutation(
    orpc.backoffice.apiKeys.rotate.mutationOptions({
      mutationKey: orpc.backoffice.apiKeys.rotate.mutationKey(),
      onSuccess: ({ key }) => {
        setApiKey(key)
        setObscured(false)
        toast.success('API Key berhasil dirotasi!')
      },
      onError: (error) => {
        toast.error(error.message)
      }
    })
  )

  const form = useForm({
    defaultValues: {
      apiKey
    },
    validators: {
      onSubmit: formSchema
    },
    onSubmit: () => rotate.mutate({ id: initialApiKey.id })
  })

  return (
    <form
      id="rotate-api-key-form"
      className="w-full"
      onSubmit={(e) => {
        e.preventDefault()
        void form.handleSubmit()
      }}
      aria-disabled={rotate.isPending}
    >
      <FieldGroup aria-disabled={rotate.isPending}>
        <form.Field
          name="apiKey"
          children={(field) => {
            const isInvalid =
              field.state.meta.isTouched && !field.state.meta.isValid

            return (
              <Field data-invalid={isInvalid} aria-disabled={rotate.isPending}>
                <InputGroup className="border-input border">
                  <InputGroupAddon align="inline-start">
                    <Key />
                  </InputGroupAddon>
                  <InputGroupInput
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    autoComplete="off"
                    type={obscured ? 'password' : 'text'}
                    placeholder="auto-generated-api-key"
                    disabled={rotate.isPending}
                    readOnly
                  />
                  <InputGroupAddon
                    align="inline-end"
                    className="pointer-events-auto"
                  >
                    <InputGroupButton aria-label="Copy" asChild>
                      <CopyButton
                        content={field.state.value}
                        onCopy={() =>
                          toast.success(
                            'API Key berhasil disalin ke clipboard!'
                          )
                        }
                        variant="ghost"
                      />
                    </InputGroupButton>
                  </InputGroupAddon>
                </InputGroup>
                {isInvalid && <FieldError errors={field.state.meta.errors} />}
                <FieldDescription className="text-xs">
                  API Key hanya ditampilkan sekali saat dibuat (rotasi).
                  Pastikan untuk menyalin dan menyimpannya dengan aman.
                </FieldDescription>
              </Field>
            )
          }}
        />
      </FieldGroup>
      <div className="mt-8 flex w-full justify-end">
        <Button
          type="button"
          variant="outline"
          className="mr-2"
          disabled={rotate.isPending}
          onClick={(e) => {
            e.preventDefault()
            onClose?.(apiKey)
          }}
        >
          Tutup
        </Button>
        <Button type="submit" disabled={rotate.isPending}>
          {rotate.isPending ? <Spinner variant="circle" /> : 'Rotasi'}
        </Button>
      </div>
    </form>
  )
}
